import { MediaManagerProvider } from '@connected/api-client/components/providers/media-manager';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useCallback, useMemo } from 'react';

import type { ElectricalProjectFile } from '@/hooks/use-electrical-project-files';

import { ElectricalProjectFilesContext } from '@/hooks/use-electrical-project-files';
import { generateMediaFileId as generateMediaFileIdFromElectricalFile } from '@/lib/electrical-file';

export type ElectricalProjectFilesProviderProps = {
    electricalProjectVersionId: string;
    children: React.ReactNode;
};

export function ElectricalProjectFilesInnerProvider({ electricalProjectVersionId, children }: ElectricalProjectFilesProviderProps) {
    const projectFilesQueryOptions = useMemo(() => elektraAppQueryOptions.project.files.listOptions({ electricalProjectVersionId }), [electricalProjectVersionId]);

    const projectFilesQuery = useSuspenseQuery(projectFilesQueryOptions);

    const getElectricalProjectFile = useCallback((electricalProjectFileId: string | null | undefined) => {
        if (!electricalProjectFileId) {
            return null;
        }
        return projectFilesQuery.data?.Files?.find(file => file.ElectricalProjectFileId === electricalProjectFileId) ?? null;
    }, [projectFilesQuery.data?.Files]);

    const generateMediaFileId = useCallback((electricalProjectFile: ElectricalProjectFile) => {
        const electricalProjectFileId = electricalProjectFile.ElectricalProjectFileId;
        if (!electricalProjectFileId) {
            throw new Error('Electrical project file ID is missing');
        }
        return generateMediaFileIdFromElectricalFile(
            electricalProjectVersionId,
            electricalProjectFileId,
            electricalProjectFile.File?.FileId ?? undefined,
        );
    }, [electricalProjectVersionId]);

    const value = useMemo<ElectricalProjectFilesContext>(() => {
        return {
            files: projectFilesQuery.data?.Files ?? [],
            generateMediaFileId,
            getElectricalProjectFile,
        };
    }, [projectFilesQuery.data?.Files, generateMediaFileId, getElectricalProjectFile]);

    return <ElectricalProjectFilesContext value={value}>{children}</ElectricalProjectFilesContext>;
}

export function ElectricalProjectFilesProvider({ fallback, ...props }: ElectricalProjectFilesProviderProps & {
    fallback: React.ReactNode;
}) {
    return (
        <MediaManagerProvider fallback={fallback}>
            <Suspense fallback={fallback}>
                <ElectricalProjectFilesInnerProvider {...props} />
            </Suspense>
        </MediaManagerProvider>
    );
}
